<script lang="ts">
  // Multi-line text field (descriptions, notes, messages).
  //   <Textarea bind:value={note} rows={3} autosize maxRows={10} />
  //   <Textarea bind:value={msg} maxlength={2000} showCount />
  // `autosize` grows the field with its content (up to `maxRows`).
  // `fill` makes the field as tall as its container (notes widget), without resize handle.
  import type { HTMLTextareaAttributes } from 'svelte/elements';
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props extends Omit<HTMLTextareaAttributes, 'value'> {
    value?: string | null;
    rows?: number;
    autosize?: boolean;
    fill?: boolean;
    maxRows?: number;
    invalid?: boolean;
    /** Show "12/2000" under the field (needs maxlength). */
    showCount?: boolean;
    el?: HTMLTextAreaElement | null;
  }
  let { value = $bindable(''), rows = 3, autosize = false, fill = false, maxRows = 12, invalid = false, showCount = false, el = $bindable(null), maxlength, class: cls = '', ...rest }: Props = $props();

  function resize() {
    if (!autosize || !el) return;
    const style = getComputedStyle(el);
    const line = parseFloat(style.lineHeight) || 20;
    const extra = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth), line * maxRows + extra)}px`;
  }

  $effect(() => {
    void value;
    if (autosize && el) resize();
  });
  const count = $derived((value || '').length);
</script>

<div class="ta {cls}" class:fill>
  <textarea
    bind:this={el}
    bind:value
    class="control"
    class:invalid
    class:autosize
    {rows}
    {maxlength}
    aria-invalid={invalid || undefined}
    {...rest}
  ></textarea>
  {#if showCount && maxlength}
    <span class="count tabular" class:near={count > Number(maxlength) * 0.9} aria-live="polite">{t('ui.characters', { count, max: maxlength })}</span>
  {/if}
</div>

<style>
  .ta { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
  /* rows decides the height (the global textarea.control has a min-height) */
  .ta textarea { min-height: 0; }
  .ta .autosize { resize: none; overflow-y: auto; }
  .fill { height: 100%; }
  .fill textarea { flex: 1; resize: none; }
  .count { align-self: flex-end; font-size: var(--text-xs); color: var(--text-3); }
  .count.near { color: var(--warning-strong); }
</style>
